import { FC } from 'react';
import { useAtomValue } from 'jotai';
import LineLogo from './icon/line.svg';
import FacebookLogo from './icon/facebook.svg';
import WhatsAppLogo from './icon/whatsapp.svg';
import TwitterLogo from './icon/x.svg';
import LinkLogo from './icon/link.svg';
import { useTranslation } from 'react-i18next';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { EClickPosition } from '~/utils/click-position';

type shareType = 'link' | 'facebook' | 'x' | 'whatsapp' | 'line';

interface ShareConfig {
  id: shareType;
  text: string;
  Icon: FC;
  color: string;
  className?: string;
  onClick: () => void;
}

const shareInOrder: Record<string, shareType[]> = {
  US: ['link', 'facebook', 'x', 'whatsapp', 'line'],
  JP: ['line', 'x', 'link', 'facebook', 'whatsapp'],
  TH: ['line', 'facebook', 'link', 'whatsapp', 'x'],
  VN: ['facebook', 'link', 'whatsapp', 'x', 'line'],
  default: ['whatsapp', 'link', 'facebook', 'x', 'line'],
};

export const useShareConfig = (props: { handleClose: () => void }): ShareConfig[] => {
  const { handleClose } = props;

  const { region } = useAtomValue($Locale);
  const { t } = useTranslation();

  const order = shareInOrder[region] || shareInOrder.default;

  const shareTitle = t('web_tab_name');
  const shareHref = `https://www.lemon8-app.com?region=${region.toLocaleLowerCase()}&pid=website_seo_share`;

  const handleShareLine = () => {
    window.Tea('wap_click', {
      current_position: EClickPosition.SHARE_PANEL_ICON,
      page_click_id: 'line',
      is_cta: 0,
    });
    window.open(`https://line.me/R/share?text=${encodeURIComponent(`${shareTitle} ${shareHref}`)}`);
  };

  const handleShareFB = () => {
    try {
      window.Tea('wap_click', {
        current_position: EClickPosition.SHARE_PANEL_ICON,
        page_click_id: 'facebook',
        is_cta: 0,
      });
      window.FB.init({
        appId: '426786581540674',
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v14.0',
      });
      window.FB.ui({ method: 'share', href: shareHref, hashtag: '#Lemon8' });
    } catch (e) {
      console.log('>>>>', e);
    }
  };

  const handleShareWhatsApp = () => {
    window.Tea('wap_click', {
      current_position: EClickPosition.SHARE_PANEL_ICON,
      page_click_id: 'whatsapp',
      is_cta: 0,
    });
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle} ${shareHref}`)}`);
  };

  const handleShareTwitter = () => {
    window.Tea('wap_click', {
      current_position: EClickPosition.SHARE_PANEL_ICON,
      page_click_id: 'twitter',
      is_cta: 0,
    });
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareHref)}`,
    );
  };

  const handleCopyLink = async () => {
    try {
      window.Tea('wap_click', {
        current_position: EClickPosition.SHARE_PANEL_ICON,
        page_click_id: 'copy_link',
        is_cta: 0,
      });
      await navigator.clipboard.writeText(shareHref);
    } catch (e) {
      console.log(e);
    }

    handleClose();
  };

  const config: ShareConfig[] = [
    {
      id: 'line',
      text: 'Line',
      Icon: LineLogo,
      color: '#00BD64',
      onClick: handleShareLine,
    },
    {
      id: 'facebook',
      text: 'Facebook',
      Icon: FacebookLogo,
      color: '#3B77EA',
      onClick: handleShareFB,
    },
    {
      id: 'whatsapp',
      text: 'WhatsApp',
      Icon: WhatsAppLogo,
      color: '#47C757',
      onClick: handleShareWhatsApp,
    },
    {
      id: 'x',
      text: 'X',
      Icon: TwitterLogo,
      color: '#1DA1F2',
      onClick: handleShareTwitter,
    },
    {
      id: 'link',
      text: t('copy_link'),
      Icon: LinkLogo,
      color: '#FFF',
      className: 'copy-link',
      onClick: handleCopyLink,
    },
  ];

  config.sort((x: ShareConfig, y: ShareConfig) => {
    const indexX = order.indexOf(x.id);
    const indexY = order.indexOf(y.id);
    return indexX - indexY;
  });

  return config;
};
